import { SyncStatus } from 'shared/domain/entitySyncStatus/syncStatus';
import { __HttpRequestModel__ } from 'shared/domain/httpRequest/httpRequestModel';
import { SetDirectorySync } from 'serviceWorker/repository/directory/setDirectorySync';
import {
  IDocumentationRestoreUploader,
  UploadDocumentationRestore,
} from 'serviceWorker/repository/documentation/uploadDocumentationRestore';
import { Identificable } from 'shared/types/commonView';
import type { IHttpRequestRunner } from 'shared/domain/httpRequest/httpRequestRunner';

export class DocumentationRestoreRequestRunner implements IHttpRequestRunner {
  constructor(
    private documentationRestoreUploader: IDocumentationRestoreUploader,
    private uploadDocumentationRestoreUseCase: UploadDocumentationRestore,
    private getDocumentations: (
      ids: number[]
    ) => Promise<Partial<Identificable>[]>,
    private getDirectories: (
      ids: number[]
    ) => Promise<(Partial<Identificable> & { localId: number })[]>,
    private setDirectorySync: SetDirectorySync
  ) {}

  execute(request: __HttpRequestModel__): Promise<boolean> {
    switch (request.method) {
      case 'GET': {
        return this.restoreDocumentation(request);
      }
    }
    throw new Error('Not implemented.');
  }

  private async restoreDocumentation(
    request: __HttpRequestModel__
  ): Promise<boolean> {
    const docIds = request.data.documentations ?? [];
    const dirIds = request.data.directories ?? [];
    const [documentations, directories] = await Promise.all([
      this.getDocumentations(docIds),
      this.getDirectories(dirIds),
    ]);

    const documentationBaseUrl = `/project/${request.data.projectId}/documentation`;
    const directoryBaseUrl = `/project/${request.data.projectId}/dir`;

    try {
      if (!documentations.every((doc) => doc._id)) {
        throw new Error('Cannot restore unuploaded documentation.');
      }
      if (!directories.every((dir) => dir._id)) {
        throw new Error('Cannot restore unuploaded directory.');
      }

      await Promise.all([
        ...documentations.map((doc) =>
          this.uploadDocumentationRestoreUseCase(
            this.documentationRestoreUploader,
            `${documentationBaseUrl}/${doc._id}/restore`
          )
        ),
        ...directories.map((dir) =>
          this.uploadDocumentationRestoreUseCase(
            this.documentationRestoreUploader,
            `${directoryBaseUrl}/${dir._id}/restore`
          )
        ),
      ]);

      await Promise.all(
        directories.map((dir) =>
          this.setDirectorySync(dir.localId, SyncStatus.SUCCESS)
        )
      );

      return true;
    } catch (e) {
      return false;
    }
  }
}
